import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pagination } from "antd";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Label } from "../../components/ui/label";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { PtsRemainingTimeRequestService } from "@/services/APIs";

const DEFAULT_PAGE_SIZE = 100;

function formatDateForApi(dateString: string): string {
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
}

function formatReason(reason: string, notFiledTask?: boolean): string {
  if (reason === "-" || notFiledTask) {
    return "Not Filled Task Hours";
  }
  return reason || "--";
}

function formatDisplayDate(dateString: string): string {
  if (!dateString) {
    return "";
  }
  try {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
}

const RemainingTimeReportPage = () => {
  const getTodayDate = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const [selectedDate, setSelectedDate] = useState<string>(() =>
    getTodayDate()
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const apiDate = formatDateForApi(selectedDate);

  const { data: reportData, isLoading } = useQuery({
    queryKey: ["remaining-time-request", apiDate, page, pageSize],
    queryFn: () =>
      PtsRemainingTimeRequestService.getRemainingTimeRequests({
        date: apiDate,
        page,
        page_size: pageSize,
      }),
    enabled: !!selectedDate,
  });

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
    setPage(1);
  };

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const records = reportData?.data ?? [];

  return (
    <div className="space-y-6 p-4 md:p-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Remaining Time Report
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {formatDisplayDate(selectedDate)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="remaining-time-date" className="text-sm font-medium">
            Date:
          </Label>
          <input
            id="remaining-time-date"
            type="date"
            value={selectedDate}
            onChange={e => handleDateChange(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-3 md:p-6">
          <div className="mb-4">
            <div className="flex flex-col items-center gap-2 rounded-md border border-gray-200 p-2 sm:inline-flex sm:flex-row sm:items-center sm:gap-3">
              <p className="text-sm font-medium">Total Employees:</p>
              <span className="text-xl font-bold text-blue-600">
                {reportData?.count ?? 0}
              </span>
            </div>
          </div>

          <div className="scrollbar-thin-grey max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <Table className="w-full min-w-[720px]">
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead className="min-w-[150px] border-r border-gray-200 px-3 py-3 font-semibold text-gray-700 sm:px-4">
                    Employee Name
                  </TableHead>
                  <TableHead className="min-w-[100px] border-r border-gray-200 px-3 py-3 font-semibold text-gray-700 sm:px-4">
                    Check In
                  </TableHead>
                  <TableHead className="min-w-[100px] border-r border-gray-200 px-3 py-3 font-semibold text-gray-700 sm:px-4">
                    Check Out
                  </TableHead>
                  <TableHead className="min-w-[120px] border-r border-gray-200 px-3 py-3 font-semibold text-gray-700 sm:px-4">
                    Remaining Time
                  </TableHead>
                  <TableHead className="min-w-[220px] px-3 py-3 font-semibold text-gray-700 sm:px-4">
                    Reason
                  </TableHead>
                  {/* <TableHead className="w-[110px] px-4 py-3 font-semibold text-gray-700">
                    Date
                  </TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No remaining time requests found for this date.
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map(record => (
                    <TableRow key={record.id} className="hover:bg-gray-50">
                      <TableCell className="border-r border-gray-200 px-3 py-3 align-top sm:px-4">
                        {record.emp_name || "--"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap border-r border-gray-200 px-3 py-3 align-top sm:px-4">
                        {record.checkin || "--"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap border-r border-gray-200 px-3 py-3 align-top sm:px-4">
                        {record.checkout ?? "--"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap border-r border-gray-200 px-3 py-3 align-top font-medium text-orange-600 sm:px-4">
                        {record.remaining_time || "--"}
                      </TableCell>
                      <TableCell
                        className={`whitespace-normal break-words px-3 py-3 align-top text-sm leading-relaxed sm:px-4 ${
                          record.reason === "-" || record.not_filed_task
                            ? "font-semibold text-red-600"
                            : ""
                        }`}
                      >
                        {formatReason(record.reason, record.not_filed_task)}
                      </TableCell>
                      {/* <TableCell className="px-4 py-3 align-top">
                        {record.date || "--"}
                      </TableCell> */}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {(reportData?.count ?? 0) > 0 && (
            <div className="mt-4 flex justify-end">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={reportData?.count ?? 0}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={handlePaginationChange}
                showTotal={total => `Total ${total} requests`}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RemainingTimeReportPage;
